// lib/submissionService.ts
import { problemBankApi } from './problemBankApi';
import { demoCompanyService, DemoSubmission } from './demoCompanyService';
import { aiDemoEnhancer } from './aiDemoEnhancer';

export interface SubmissionData {
  // Contact Information
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  contactDepartment?: string;
  yearsInRole?: string;
  decisionMaker?: string;
  decisionMakerDetails?: string;
  secondaryName?: string;
  secondaryTitle?: string;
  secondaryEmail?: string;
  secondaryPhone?: string;

  // Problem Details
  problemTitle: string;
  problemCategory: string;
  otherCategory?: string;
  plainDescription: string;
  rootCause: string;
  firstAppeared: string;
  frequency: string;
  seasonalMonths?: string;
  scope: string;
  previousAttempts?: string;

  // Financial Impact
  costEnergy?: number;
  costMaterial?: number;
  costDowntime?: number;
  costLabour?: number;
  costCompliance?: number;
  costOther?: number;
  costOtherDescription?: string;
  totalAnnualCost: number;
  evidence: string[];
  otherEvidence?: string;
  confidenceLevel: string;
  estimateDerivation?: string;

  // Scalability
  industryPrevalence: string;
  estimatedKenya?: number;
  estimatedEastAfrica?: number;
  affectedIndustries?: string;
  scalabilityFactor?: string;
  existingSolutions?: string;
  soughtHelp?: string[];

  // Pilot Readiness
  pilotLikelihood: string;
  approvalAuthority?: string;
  approvalTimeline?: string;
  pilotDuration?: string;
  successCriteria?: string;
  budgetRange?: string;
  commercialModel?: string;
  pilotAccess?: string[];

  // Technical Environment
  facilityAge?: string;
  facilitySize?: string;
  operatingHours?: string;
  operatingDays?: string;
  monitoringSystems?: string[];
  connectivity?: string;
  primaryEquipment?: string;
  equipmentBrand?: string;
  equipmentAge?: string;
  maintenanceSpend?: number;
  lastOverhaul?: string;
  solutionType?: string[];
  technicalConstraints?: string;

  // Regulatory Context
  regulatoryRelated?: string;
  regulatoryBodies?: string;
  certifications?: string[];
  upcomingDeadlines?: string;

  // Self-Assessment
  severityRating: string;
  urgencyRating: string;
  prevalenceRating: string;
  dataAvailabilityRating: string;
  techOpennessRating: string;
  managementSupportRating: string;
  decisionSpeedRating: string;
  additionalContext?: string;

  // Consent
  consentStorage: boolean;
  consentContact: boolean;
  consentAnonymised: boolean;
  consentUpdates: boolean;
  consentSquad: boolean;
  dataSharingLevel: string;
  declarationAgreed: boolean;

  // Files
  documents?: File[];
}

export interface SubmissionResponse {
  id: string;
  status: 'pending' | 'under_review' | 'accepted' | 'rejected';
  submittedAt: string;
  problemBankId?: string;
  message?: string;
}

export const submissionService = {
  /**
   * Submit a problem to the problem bank (works for both demo and production)
   * NORMAL MODE: Saves EXACT user data, no defaults, no AI
   * DEMO MODE: Enhances with AI for rich demo experience
   */
  submitProblem: async (
    submissionData: SubmissionData,
    companyId?: string,
    isDemoMode: boolean = false
  ): Promise<SubmissionResponse> => {
    
    // If in demo mode, use demo service with AI enhancements
    if (isDemoMode) {
      return submissionService.submitDemoProblem(submissionData, companyId);
    }

    // Otherwise, submit to actual API - NO AI, NO DEFAULTS
    return submissionService.submitToProduction(submissionData);
  },

  /**
   * Submit to production API
   * CRITICAL: Preserves EXACT user data, no defaults, no AI enhancements
   * Only includes fields that the user actually filled in
   */
  submitToProduction: async (submissionData: SubmissionData): Promise<SubmissionResponse> => {
    try {
      // Format the data for the API - ONLY include fields that have values
      // This preserves EXACTLY what the user entered
      const apiSubmission = {
        contact: {
          name: submissionData.contactName,
          title: submissionData.contactTitle,
          email: submissionData.contactEmail,
          phone: submissionData.contactPhone,
          ...(submissionData.contactDepartment && { department: submissionData.contactDepartment }),
          ...(submissionData.yearsInRole && { yearsInRole: submissionData.yearsInRole }),
          ...(submissionData.decisionMaker && { decisionMaker: submissionData.decisionMaker }),
          ...(submissionData.decisionMakerDetails && { decisionMakerDetails: submissionData.decisionMakerDetails }),
          ...(submissionData.secondaryName && {
            secondaryContact: {
              name: submissionData.secondaryName,
              title: submissionData.secondaryTitle,
              email: submissionData.secondaryEmail,
              phone: submissionData.secondaryPhone
            }
          })
        },
        problem: {
          title: submissionData.problemTitle,
          category: submissionData.problemCategory,
          ...(submissionData.otherCategory && { otherCategory: submissionData.otherCategory }),
          description: submissionData.plainDescription,
          rootCause: submissionData.rootCause,
          firstAppeared: submissionData.firstAppeared,
          frequency: submissionData.frequency,
          ...(submissionData.seasonalMonths && { seasonalMonths: submissionData.seasonalMonths }),
          scope: submissionData.scope,
          ...(submissionData.previousAttempts && { previousAttempts: submissionData.previousAttempts })
        },
        financial: {
          ...(submissionData.costEnergy && { energy: submissionData.costEnergy }),
          ...(submissionData.costMaterial && { material: submissionData.costMaterial }),
          ...(submissionData.costDowntime && { downtime: submissionData.costDowntime }),
          ...(submissionData.costLabour && { labour: submissionData.costLabour }),
          ...(submissionData.costCompliance && { compliance: submissionData.costCompliance }),
          ...(submissionData.costOther && { other: submissionData.costOther }),
          ...(submissionData.costOtherDescription && { otherDescription: submissionData.costOtherDescription }),
          totalAnnual: submissionData.totalAnnualCost,
          evidence: submissionData.evidence,
          ...(submissionData.otherEvidence && { otherEvidence: submissionData.otherEvidence }),
          confidenceLevel: submissionData.confidenceLevel,
          ...(submissionData.estimateDerivation && { estimateDerivation: submissionData.estimateDerivation })
        },
        scalability: {
          industryPrevalence: submissionData.industryPrevalence,
          ...(submissionData.estimatedKenya && { estimatedKenya: submissionData.estimatedKenya }),
          ...(submissionData.estimatedEastAfrica && { estimatedEastAfrica: submissionData.estimatedEastAfrica }),
          ...(submissionData.affectedIndustries && { affectedIndustries: submissionData.affectedIndustries }),
          ...(submissionData.scalabilityFactor && { scalabilityFactor: submissionData.scalabilityFactor }),
          ...(submissionData.existingSolutions && { existingSolutions: submissionData.existingSolutions }),
          ...(submissionData.soughtHelp && { soughtHelp: submissionData.soughtHelp })
        },
        pilot: {
          likelihood: submissionData.pilotLikelihood,
          ...(submissionData.approvalAuthority && { approvalAuthority: submissionData.approvalAuthority }),
          ...(submissionData.approvalTimeline && { approvalTimeline: submissionData.approvalTimeline }),
          ...(submissionData.pilotDuration && { pilotDuration: submissionData.pilotDuration }),
          ...(submissionData.successCriteria && { successCriteria: submissionData.successCriteria }),
          ...(submissionData.budgetRange && { budgetRange: submissionData.budgetRange }),
          ...(submissionData.commercialModel && { commercialModel: submissionData.commercialModel }),
          ...(submissionData.pilotAccess && { access: submissionData.pilotAccess })
        },
        technical: {
          ...(submissionData.facilityAge && { facilityAge: submissionData.facilityAge }),
          ...(submissionData.facilitySize && { facilitySize: submissionData.facilitySize }),
          ...(submissionData.operatingHours && { operatingHours: submissionData.operatingHours }),
          ...(submissionData.operatingDays && { operatingDays: submissionData.operatingDays }),
          ...(submissionData.monitoringSystems && { monitoringSystems: submissionData.monitoringSystems }),
          ...(submissionData.connectivity && { connectivity: submissionData.connectivity }),
          ...(submissionData.primaryEquipment && { primaryEquipment: submissionData.primaryEquipment }),
          ...(submissionData.equipmentBrand && { equipmentBrand: submissionData.equipmentBrand }),
          ...(submissionData.equipmentAge && { equipmentAge: submissionData.equipmentAge }),
          ...(submissionData.maintenanceSpend && { maintenanceSpend: submissionData.maintenanceSpend }),
          ...(submissionData.lastOverhaul && { lastOverhaul: submissionData.lastOverhaul }),
          ...(submissionData.solutionType && { solutionType: submissionData.solutionType }),
          ...(submissionData.technicalConstraints && { technicalConstraints: submissionData.technicalConstraints })
        },
        regulatory: {
          ...(submissionData.regulatoryRelated && { related: submissionData.regulatoryRelated }),
          ...(submissionData.regulatoryBodies && { bodies: submissionData.regulatoryBodies }),
          ...(submissionData.certifications && { certifications: submissionData.certifications }),
          ...(submissionData.upcomingDeadlines && { upcomingDeadlines: submissionData.upcomingDeadlines })
        },
        selfAssessment: {
          severity: submissionData.severityRating,
          urgency: submissionData.urgencyRating,
          prevalence: submissionData.prevalenceRating,
          dataAvailability: submissionData.dataAvailabilityRating,
          techOpenness: submissionData.techOpennessRating,
          managementSupport: submissionData.managementSupportRating,
          decisionSpeed: submissionData.decisionSpeedRating,
          ...(submissionData.additionalContext && { additionalContext: submissionData.additionalContext })
        },
        consent: {
          storage: submissionData.consentStorage,
          contact: submissionData.consentContact,
          anonymised: submissionData.consentAnonymised,
          updates: submissionData.consentUpdates,
          squad: submissionData.consentSquad,
          dataSharingLevel: submissionData.dataSharingLevel,
          declarationAgreed: submissionData.declarationAgreed
        }
      };

      // Submit to API
      const response = await problemBankApi.submitExternalProblem(apiSubmission);
      
      // Handle file uploads if any
      if (submissionData.documents && submissionData.documents.length > 0) {
        await submissionService.uploadDocuments(response.id, submissionData.documents);
      }

      return {
        id: response.id,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        problemBankId: response.id,
        message: 'Submission received successfully'
      };
    } catch (error) {
      console.error('Production submission failed:', error);
      throw new Error('Failed to submit problem. Please try again.');
    }
  },

  /**
   * Submit in demo mode with AI enhancements
   * DEMO ONLY: Enhances the submission with rich, realistic data
   * Still preserves the user's core input but adds demo-specific details
   */
  submitDemoProblem: async (
    submissionData: SubmissionData,
    companyId?: string
  ): Promise<SubmissionResponse> => {
    try {
      // Get current company for context
      const company = demoCompanyService.getCurrentCompany();
      
      if (!company && !companyId) {
        throw new Error('Company not found');
      }

      // Calculate urgency based on pilot likelihood
      const urgency = submissionData.pilotLikelihood === 'Very likely — we would pilot immediately' ? 'high' : 
                      submissionData.pilotLikelihood === 'Likely — subject to internal approval' ? 'medium' : 'low';

      // Create base submission with user's EXACT data - NO DEFAULTS
      const baseSubmission = {
        id: `sub-${Date.now()}`,
        companyId: companyId || 'demo-company',
        companyName: company?.name || 'Demo Company',
        companyRegistration: company?.registrationNumber,
        
        // Basic fields - use submissionData directly, no defaults
        problemTitle: submissionData.problemTitle,
        description: submissionData.plainDescription,
        plainDescription: submissionData.plainDescription,
        estimatedImpact: `KES ${submissionData.totalAnnualCost?.toLocaleString() || 0} per year`,
        estimatedImpactValue: submissionData.totalAnnualCost || 0,
        urgency: urgency,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        documents: submissionData.documents?.map(f => ({
          name: f.name,
          url: URL.createObjectURL(f),
          size: `${(f.size / 1024 / 1024).toFixed(1)} MB`,
          type: f.type.split('/')[1]?.toUpperCase() || 'File'
        })) || [],
        category: submissionData.problemCategory ? [submissionData.problemCategory] : ['other'],
        
        // Pass through ALL user data exactly as submitted
        // No defaults, no overrides
        contactName: submissionData.contactName,
        contactTitle: submissionData.contactTitle,
        contactEmail: submissionData.contactEmail,
        contactPhone: submissionData.contactPhone,
        contactDepartment: submissionData.contactDepartment,
        yearsInRole: submissionData.yearsInRole,
        decisionMaker: submissionData.decisionMaker,
        decisionMakerDetails: submissionData.decisionMakerDetails,
        secondaryName: submissionData.secondaryName,
        secondaryTitle: submissionData.secondaryTitle,
        secondaryEmail: submissionData.secondaryEmail,
        secondaryPhone: submissionData.secondaryPhone,

        problemCategory: submissionData.problemCategory,
        otherCategory: submissionData.otherCategory,
        rootCause: submissionData.rootCause,
        firstAppeared: submissionData.firstAppeared,
        frequency: submissionData.frequency,
        seasonalMonths: submissionData.seasonalMonths,
        scope: submissionData.scope,
        previousAttempts: submissionData.previousAttempts,

        costEnergy: submissionData.costEnergy,
        costMaterial: submissionData.costMaterial,
        costDowntime: submissionData.costDowntime,
        costLabour: submissionData.costLabour,
        costCompliance: submissionData.costCompliance,
        costOther: submissionData.costOther,
        costOtherDescription: submissionData.costOtherDescription,
        totalAnnualCost: submissionData.totalAnnualCost,
        evidence: submissionData.evidence,
        otherEvidence: submissionData.otherEvidence,
        confidenceLevel: submissionData.confidenceLevel,
        estimateDerivation: submissionData.estimateDerivation,

        industryPrevalence: submissionData.industryPrevalence,
        estimatedKenya: submissionData.estimatedKenya,
        estimatedEastAfrica: submissionData.estimatedEastAfrica,
        affectedIndustries: submissionData.affectedIndustries,
        scalabilityFactor: submissionData.scalabilityFactor,
        existingSolutions: submissionData.existingSolutions,
        soughtHelp: submissionData.soughtHelp,

        pilotLikelihood: submissionData.pilotLikelihood,
        approvalAuthority: submissionData.approvalAuthority,
        approvalTimeline: submissionData.approvalTimeline,
        pilotDuration: submissionData.pilotDuration,
        successCriteria: submissionData.successCriteria,
        budgetRange: submissionData.budgetRange,
        commercialModel: submissionData.commercialModel,
        pilotAccess: submissionData.pilotAccess,

        facilityAge: submissionData.facilityAge,
        facilitySize: submissionData.facilitySize,
        operatingHours: submissionData.operatingHours,
        operatingDays: submissionData.operatingDays,
        monitoringSystems: submissionData.monitoringSystems,
        connectivity: submissionData.connectivity,
        primaryEquipment: submissionData.primaryEquipment,
        equipmentBrand: submissionData.equipmentBrand,
        equipmentAge: submissionData.equipmentAge,
        maintenanceSpend: submissionData.maintenanceSpend,
        lastOverhaul: submissionData.lastOverhaul,
        solutionType: submissionData.solutionType,
        technicalConstraints: submissionData.technicalConstraints,

        regulatoryRelated: submissionData.regulatoryRelated,
        regulatoryBodies: submissionData.regulatoryBodies,
        certifications: submissionData.certifications,
        upcomingDeadlines: submissionData.upcomingDeadlines,

        severityRating: submissionData.severityRating,
        urgencyRating: submissionData.urgencyRating,
        prevalenceRating: submissionData.prevalenceRating,
        dataAvailabilityRating: submissionData.dataAvailabilityRating,
        techOpennessRating: submissionData.techOpennessRating,
        managementSupportRating: submissionData.managementSupportRating,
        decisionSpeedRating: submissionData.decisionSpeedRating,
        additionalContext: submissionData.additionalContext,

        consentStorage: submissionData.consentStorage,
        consentContact: submissionData.consentContact,
        consentAnonymised: submissionData.consentAnonymised,
        consentUpdates: submissionData.consentUpdates,
        consentSquad: submissionData.consentSquad,
        dataSharingLevel: submissionData.dataSharingLevel,
        declarationAgreed: submissionData.declarationAgreed,
      };

      console.log('📝 Demo submission base data:', {
        contactName: baseSubmission.contactName,
        problemTitle: baseSubmission.problemTitle,
        totalAnnualCost: baseSubmission.totalAnnualCost
      });

      // ENHANCE for demo mode - add rich demo data while preserving user input
      // This ONLY happens in demo mode and makes the demo experience richer
      const enhancedSubmission = await aiDemoEnhancer.enhanceDemoSubmission(baseSubmission, company);

      console.log('📝 Enhanced demo submission:', {
        contactName: enhancedSubmission.contactName,
        hasRootCause: !!enhancedSubmission.rootCause,
        hasMarketData: !!enhancedSubmission.estimatedKenya
      });

      // Save to localStorage
      const existingSubmissions = localStorage.getItem('demo_company_submissions');
      const submissions = existingSubmissions ? JSON.parse(existingSubmissions) : [];
      submissions.push(enhancedSubmission);
      localStorage.setItem('demo_company_submissions', JSON.stringify(submissions));

      return {
        id: enhancedSubmission.id,
        status: 'pending',
        submittedAt: enhancedSubmission.submittedAt,
        message: 'Demo submission saved with AI enhancements'
      };
    } catch (error) {
      console.error('Demo submission failed:', error);
      // Fallback to saving without AI if enhancement fails
      try {
        const fallbackSubmission = {
          id: `sub-${Date.now()}`,
          companyId: companyId || 'demo-company',
          companyName: 'Demo Company',
          problemTitle: submissionData.problemTitle,
          description: submissionData.plainDescription,
          plainDescription: submissionData.plainDescription,
          estimatedImpact: `KES ${submissionData.totalAnnualCost?.toLocaleString() || 0} per year`,
          estimatedImpactValue: submissionData.totalAnnualCost || 0,
          urgency: 'medium',
          status: 'pending',
          submittedAt: new Date().toISOString(),
          documents: [],
          category: ['other'],
          ...submissionData
        };
        
        const submissions = JSON.parse(localStorage.getItem('demo_company_submissions') || '[]');
        submissions.push(fallbackSubmission);
        localStorage.setItem('demo_company_submissions', JSON.stringify(submissions));
        
        return {
          id: fallbackSubmission.id,
          status: 'pending',
          submittedAt: fallbackSubmission.submittedAt,
          message: 'Demo submission saved (without enhancements)'
        };
      } catch (fallbackError) {
        throw new Error('Failed to save demo submission.');
      }
    }
  },

  /**
   * Upload documents for a submission
   */
  uploadDocuments: async (submissionId: string, files: File[]): Promise<void> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('documents', file);
    });
    
    try {
      await problemBankApi.uploadDocuments(submissionId, formData);
    } catch (error) {
      console.error('Document upload failed:', error);
      // Don't throw - we still want the submission to go through
    }
  },

  /**
   * Check if currently in demo mode
   */
  isDemoMode: (): boolean => {
    return localStorage.getItem('demo_company_mode_active') === 'true' ||
           !!localStorage.getItem('demo_company_token');
  },

  /**
   * Get company info for the current session
   */
  getCompanyInfo: () => {
    const isDemo = submissionService.isDemoMode();
    if (isDemo) {
      return demoCompanyService.getCurrentCompany();
    }
    // For production, fetch from API
    return null;
  }
};